import React, { useState, useEffect } from "react";
import axios from "axios";
import { Checkbox, Button, Spin } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import "./RoleBasedApplications.css";

const rolesApi = process.env.REACT_APP_API_Roles;
const applicationsApi = process.env.REACT_APP_API_AllApps; // Same as role config
const countriesApi = process.env.REACT_APP_API_UpdatedCountries;

/**
 * Select Applications modal for role-based testers.
 * Shows only apps allowed per the role's country_settings (apps_mode + application_ids).
 */
const RoleBasedApplications = ({ numbers }) => {
  const [countrySections, setCountrySections] = useState([]); // [{ countryName, apps: [{ id, name }] }]
  const [selectedById, setSelectedById] = useState({}); // { [appId]: boolean }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const roleId = localStorage.getItem("roleId");
        if (!roleId) {
          setCountrySections([]);
          return;
        }

        const [roleRes, appsRes, countriesRes] = await Promise.all([
          axios.get(`${rolesApi}/${roleId}`),
          axios.get(applicationsApi),
          axios.get(countriesApi),
        ]);

        const roleData = roleRes.data?.data ?? roleRes.data?.body ?? roleRes.data;
        const allApps = appsRes.data?.body ?? appsRes.data ?? [];
        const countries = countriesRes.data?.body ?? countriesRes.data ?? [];

        const countrySettings = roleData?.country_settings ?? [];
        if (countrySettings.length === 0) {
          setCountrySections([]);
          return;
        }

        const allAppIds = new Set(allApps.map((a) => a.id));
        const sections = [];

        for (const cs of countrySettings) {
          const country = countries.find((c) => c.id === cs.country_id);
          const countryName = country?.name ?? `Country ${cs.country_id}`;

          let allowedIds;
          if (cs.apps_mode === "all") {
            allowedIds = allAppIds;
          } else if (cs.apps_mode === "only_selected") {
            allowedIds = new Set(cs.application_ids ?? []);
          } else if (cs.apps_mode === "except_selected") {
            const except = new Set(cs.application_ids ?? []);
            allowedIds = new Set([...allAppIds].filter((id) => !except.has(id)));
          } else {
            allowedIds = allAppIds;
          }

          const allowedApps = allApps
            .filter((a) => allowedIds.has(a.id))
            .map((a) => ({ id: a.id, name: a.name }));

          if (allowedApps.length > 0) {
            sections.push({ countryName, apps: allowedApps });
          }
        }

        setCountrySections(sections);
        setSelectedById({});
      } catch (error) {
        console.error("Error fetching role/apps:", error);
        setCountrySections([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleToggle = (id) => {
    setSelectedById((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const allSelected =
    countrySections.length > 0 &&
    countrySections.every((sec) => sec.apps.every((a) => selectedById[a.id]));
  const someSelected =
    countrySections.length > 0 &&
    countrySections.some((sec) => sec.apps.some((a) => selectedById[a.id]));

  const handleToggleAll = () => {
    const newVal = !allSelected;
    const next = {};
    countrySections.forEach((sec) => {
      sec.apps.forEach((a) => {
        next[a.id] = newVal;
      });
    });
    setSelectedById(next);
  };

  const handleDownloadCSV = () => {
    const allApps = countrySections.flatMap((s) => s.apps);
    const uniqueApps = Array.from(new Map(allApps.map((a) => [a.id, a])).values());
    const selectedSites = uniqueApps.filter((a) => selectedById[a.id]);
    const selectedSiteNames = selectedSites.map((s) => s.name);
    let csvData = `Number,${selectedSiteNames.join(",")}\n`;
    (numbers || []).forEach((number) => {
      let rowData = `${number},`;
      selectedSites.forEach(() => {
        rowData += ",";
      });
      csvData += `${rowData}\n`;
    });
    const storedUser = localStorage.getItem("user");
    const user = JSON.parse(storedUser || "{}") || {};
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString("en-GB");
    link.download = `${user.name || "user"}-${formattedDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin tip="Loading applications..." size="large" />
      </div>
    );
  }

  return (
    <div className="rba-modal">
      <div className="rba-select-all">
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={handleToggleAll}
        >
          Select All
        </Checkbox>
      </div>
      {countrySections.length > 0 ? (
        countrySections.map((section) => (
          <div key={section.countryName} className="rba-section">
            <h3 className="rba-section-title">{section.countryName}</h3>
            <div className="rba-apps-grid">
              {section.apps.map((site) => (
                <Checkbox
                  key={`${section.countryName}-${site.id}`}
                  checked={!!selectedById[site.id]}
                  onChange={() => handleToggle(site.id)}
                  className="rba-app-checkbox"
                >
                  {site.name}
                </Checkbox>
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="rba-empty">No applications configured for your role.</div>
      )}
      <div className="rba-actions">
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadCSV}
          className="rba-download-btn"
        >
          Download as CSV
        </Button>
      </div>
    </div>
  );
};

export default RoleBasedApplications;
